/**
 * (2026-08-04) Bulk Add Items — admin page.
 *
 * Creates multiple sibling folders under one parent in a single
 * request, all pre-tagged with a chosen category. Because
 * `_maybe_auto_apply_presets` fires per create, any saved folder
 * preset targeting that category (or targeting an ancestor of the
 * parent with `include_descendants:true`) will fan out to every new
 * folder — Cabins sub-folder, standard attributes, etc.
 *
 * Backend: POST /api/atlas/admin/topics/bulk-create
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, FolderPlus, Loader2, Check, X as CloseIcon,
  AlertCircle, Search, ChevronRight, ChevronDown, Info,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAtlasCategories } from "../atlasCategoriesCache";


// ─── Compact parent picker — search + hit list ─────────────────
// Not a full tree — this page is admin-only, and a search field is
// faster for large taxonomies. Empty query returns the top 20 roots.
function ParentPicker({ value, valueName, onChange }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/atlas/topics?q=${encodeURIComponent(q || "")}&limit=20`
      );
      setHits(data?.topics || []);
    } catch (_e) {
      setHits([]);
    } finally { setLoading(false); }
  }, [q]);
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="rounded-lg border border-tb-navy/15 p-3 bg-white space-y-2">
      <div className="flex items-center gap-2">
        <Search className="w-4 h-4 text-tb-navy/60" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search folders by name…"
          data-testid="atlas-bulk-add-parent-search"
          className="flex-1 h-8 px-2 rounded border border-tb-navy/20 focus:border-tb-navy outline-none font-hand text-sm bg-white" />
        {value && (
          <button type="button" onClick={() => onChange(null, null)}
            data-testid="atlas-bulk-add-parent-clear"
            aria-label="Clear parent"
            className="h-7 px-2 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-[11px]">
            Create as root
          </button>
        )}
      </div>
      {value && (
        <div className="text-xs font-hand text-tb-navy/70 flex items-center gap-1"
             data-testid="atlas-bulk-add-parent-selected">
          <Check className="w-3.5 h-3.5 text-tb-navy/70" />
          Parent: <b>{valueName || value}</b>
        </div>
      )}
      <div className="max-h-52 overflow-y-auto space-y-0.5">
        {loading && (
          <div className="text-[11px] font-hand text-tb-navy/50 flex items-center gap-1 px-1 py-0.5">
            <Loader2 className="w-3 h-3 animate-spin" /> loading…
          </div>
        )}
        {!loading && hits.length === 0 && (
          <div className="text-[11px] font-hand text-tb-navy/50 px-1">No matches.</div>
        )}
        {hits.map((t) => (
          <button key={t.slug} type="button"
            onClick={() => onChange(t.slug, t.name)}
            data-testid={`atlas-bulk-add-parent-pick-${t.slug}`}
            className={
              "w-full text-left px-2 py-1.5 rounded font-hand text-sm " +
              (value === t.slug
                ? "bg-tb-navy text-tb-gold-bright"
                : "text-tb-navy hover:bg-tb-navy/5")
            }>
            {t.name} <span className="text-[10px] opacity-60">/ {t.slug}</span>
          </button>
        ))}
      </div>
    </div>
  );
}


export default function AtlasBulkAddPage() {
  // (2026-08-04, bugfix) `useAtlasCategories()` returns the array
  // directly — not an object. Previously destructured as
  // `{ categories }` which set the local to `undefined` and rendered
  // "No categories" even though 30 exist in Mongo. Every other
  // consumer (AtlasTopicShared, AtlasTopicPages, AtlasCategories,
  // AtlasPresets) reads it as an array; matching that convention.
  const categories = useAtlasCategories();
  // (2026-08-04) Deep-link support — the tree kebab "Bulk add items…"
  // navigates here with ?parent_slug=…&parent_name=… so the parent
  // picker is pre-filled and the admin lands ready to paste names.
  const [parentSlug, setParentSlug] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("parent_slug"); }
    catch { return null; }
  });
  const [parentName, setParentName] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("parent_name"); }
    catch { return null; }
  });
  const [categoryId, setCategoryId] = useState("");
  const [rawText, setRawText] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Parse names — split on newlines OR commas, trim, dedupe.
  const parsedNames = useMemo(() => {
    const raw = (rawText || "").split(/[,\n\r]+/).map((s) => s.trim()).filter(Boolean);
    const seen = new Set();
    const out = [];
    for (const n of raw) {
      const k = n.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(n);
    }
    return out;
  }, [rawText]);

  const chosenCategory = useMemo(
    () => (categories || []).find((c) => c.id === categoryId) || null,
    [categories, categoryId],
  );

  const canSubmit = parsedNames.length > 0 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setLastResult(null);
    try {
      const { data } = await api.post("/atlas/admin/topics/bulk-create", {
        parent_slug: parentSlug || null,
        category_id: categoryId || null,
        names: parsedNames,
      });
      setLastResult(data);
      const created = (data?.created || []).length;
      const skipped = (data?.skipped || []).length;
      const totalAutoApplied = (data?.created || []).reduce(
        (n, r) => n + (r.auto_applied_presets?.count || 0), 0);
      toast.success(
        `Created ${created} folder${created === 1 ? "" : "s"}` +
        (skipped ? ` · skipped ${skipped}` : "") +
        (totalAutoApplied ? ` · auto-applied ${totalAutoApplied} preset run${totalAutoApplied === 1 ? "" : "s"}` : "")
      );
      // Nudge any mounted tree caches.
      try { window.dispatchEvent(new Event("atlas:tree-invalidated")); } catch {}
      // Reset the textarea but keep parent + category selection so
      // the admin can bulk-add another batch quickly.
      setRawText("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk create failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl" data-testid="atlas-bulk-add-page">
      <div className="mb-2">
        <Link to="/atlas/topics"
              data-testid="atlas-bulk-add-back"
              className="inline-flex items-center gap-1 text-tb-navy/60 hover:text-tb-navy text-xs font-hand">
          <ArrowLeft className="w-3 h-3" /> Back to Topics
        </Link>
      </div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h1 className="text-tb-navy text-3xl md:text-4xl font-semibold flex items-center gap-3"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            <FolderPlus className="w-7 h-7 text-tb-gold-bright" />
            Bulk add items
          </h1>
          <p className="mt-1 font-hand text-tb-navy/60 text-sm max-w-2xl">
            Create multiple sibling folders under one parent in a single go,
            all pre-tagged with the same category. Saved presets that target
            that category will auto-apply their sub-folders + attributes to
            every new folder — no need to run the preset by hand.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Section 1 — parent picker */}
        <section data-testid="atlas-bulk-add-section-parent">
          <h2 className="text-tb-navy text-sm font-semibold mb-2 font-hand uppercase tracking-widest">
            1. Parent folder (optional)
          </h2>
          <p className="text-xs font-hand text-tb-navy/60 mb-2">
            Leave empty to create root-level folders.
          </p>
          <ParentPicker value={parentSlug} valueName={parentName}
            onChange={(slug, name) => { setParentSlug(slug); setParentName(name); }} />
        </section>

        {/* Section 2 — category picker */}
        <section data-testid="atlas-bulk-add-section-category">
          <h2 className="text-tb-navy text-sm font-semibold mb-2 font-hand uppercase tracking-widest">
            2. Category tag
          </h2>
          <p className="text-xs font-hand text-tb-navy/60 mb-2">
            Optional. Presets matching this category fire per new folder.
          </p>
          <select value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            data-testid="atlas-bulk-add-category-select"
            className="w-full h-10 px-3 rounded-full border border-tb-navy/20 focus:border-tb-navy outline-none font-hand text-sm bg-white text-tb-navy">
            <option value="">— No category —</option>
            {(categories || []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </section>
      </div>

      <section className="mt-6" data-testid="atlas-bulk-add-section-names">
        <h2 className="text-tb-navy text-sm font-semibold mb-2 font-hand uppercase tracking-widest">
          3. Names (one per line or comma-separated)
        </h2>
        <textarea value={rawText}
          onChange={(e) => setRawText(e.target.value)}
          rows={8}
          placeholder={"e.g.\nSymphony of the Seas\nOasis of the Seas\nWonder of the Seas"}
          data-testid="atlas-bulk-add-names-input"
          className="w-full rounded-lg border border-tb-navy/20 focus:border-tb-navy outline-none font-hand text-sm p-3 bg-white text-tb-navy" />
        <div className="mt-1 text-[11px] font-hand text-tb-navy/50">
          Blank lines are ignored. Duplicates (case-insensitive) are collapsed.
        </div>
      </section>

      {/* Preview */}
      <div className="mt-4 rounded-lg border border-tb-navy/15 p-4 bg-white"
           data-testid="atlas-bulk-add-preview">
        <div className="text-tb-navy text-sm font-semibold mb-1"
             style={{ fontFamily: '"Playfair Display", serif' }}>
          Preview
        </div>
        {parsedNames.length === 0 ? (
          <div className="text-xs font-hand text-tb-navy/55 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" /> Paste at least one name above.
          </div>
        ) : (
          <div className="text-xs font-hand text-tb-navy/70">
            Will create{" "}
            <b data-testid="atlas-bulk-add-preview-count">{parsedNames.length}</b>{" "}
            folder{parsedNames.length === 1 ? "" : "s"}{" "}
            {parentSlug ? <>under <b>{parentName || parentSlug}</b></> : <>as <b>root</b></>}
            {chosenCategory && <>, tagged <b>{chosenCategory.name}</b></>}.
            <div className="mt-2 flex flex-wrap gap-1">
              {parsedNames.slice(0, 30).map((n, i) => (
                <span key={i} className="inline-flex px-2 h-6 items-center rounded-full bg-tb-navy/10 text-tb-navy">
                  {n}
                </span>
              ))}
              {parsedNames.length > 30 && (
                <span className="inline-flex px-2 h-6 items-center text-tb-navy/50">
                  …and {parsedNames.length - 30} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button type="button" onClick={submit} disabled={!canSubmit}
          data-testid="atlas-bulk-add-submit"
          className="h-11 px-6 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm inline-flex items-center gap-1.5 disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4" />}
          {busy ? "Creating…" : `Create ${parsedNames.length || ""} folder${parsedNames.length === 1 ? "" : "s"}`}
        </button>
      </div>

      {lastResult && (
        <div className="mt-6 rounded-lg border border-tb-navy/15 p-4 bg-white"
             data-testid="atlas-bulk-add-last-result">
          <div className="text-tb-navy text-sm font-semibold mb-2"
               style={{ fontFamily: '"Playfair Display", serif' }}>
            Last run
          </div>
          <div className="text-xs font-hand text-tb-navy/80">
            Requested <b>{lastResult.requested_count}</b> · created{" "}
            <b data-testid="atlas-bulk-add-created-count">{(lastResult.created || []).length}</b> ·{" "}
            skipped <b>{(lastResult.skipped || []).length}</b>
          </div>
          {(lastResult.created || []).length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] font-hand uppercase tracking-widest text-tb-navy/50 mb-1">Created</div>
              <ul className="space-y-0.5 text-xs font-hand text-tb-navy/80">
                {(lastResult.created || []).map((r) => (
                  <li key={r.slug} data-testid={`atlas-bulk-add-created-${r.slug}`}>
                    <Check className="w-3 h-3 inline text-tb-navy/70 mr-1" />
                    {r.name} <span className="opacity-60 text-[10px]">/ {r.slug}</span>
                    {r.auto_applied_presets?.count > 0 && (
                      <span className="ml-2 inline-flex items-center gap-1 text-tb-gold-bright/90 text-[10px]">
                        <Info className="w-3 h-3" />
                        +{r.auto_applied_presets.added_folders} sub-folder{r.auto_applied_presets.added_folders === 1 ? "" : "s"},{" "}
                        +{r.auto_applied_presets.added_attributes} attr
                        {r.auto_applied_presets.added_attributes === 1 ? "" : "s"} from {r.auto_applied_presets.count} preset
                        {r.auto_applied_presets.count === 1 ? "" : "s"}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(lastResult.skipped || []).length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] font-hand uppercase tracking-widest text-tb-navy/50 mb-1">Skipped</div>
              <ul className="space-y-0.5 text-xs font-hand text-tb-navy/70">
                {(lastResult.skipped || []).map((r, i) => (
                  <li key={i} data-testid={`atlas-bulk-add-skipped-${i}`}>
                    <CloseIcon className="w-3 h-3 inline text-red-700/70 mr-1" />
                    {r.name} — <span className="opacity-70">{r.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
